import React from "react";

import { RichtextBlockModel } from "@courses-models/course";

import { colors } from "@apps/viewer/src/ts/config";
import { injectStore, Store } from "@apps/viewer/src/ts/store";

import { Block } from "@apps/viewer/src/ts/components/block/Block";

type RichtextBlockProps = {
    /**
     * A reference to the store that's backing the application lifecycle state.
     */
    store: Store;

    /**
     * A reference to the model representing the block that's currently being
     * rendered.
     */
    model: RichtextBlockModel;

    /**
     * Contains the parsed content from the model, ready for rendering (so we
     * don't run into issues with React components not supporting async
     * rendering).
     */
    content: React.ReactNode[];
};

/**
 * Primary root of the render tree for the content of a single block.
 *
 * This component will render generic content (ie. the teacher note on top, if
 * any was defined) and then propagate rendering of the actual content to the
 * designated block sub components.
 */
class RichtextBlockUnwrapped extends Block<RichtextBlockProps> {
    /**
     * Renderer that injects the content as entered by the user himself.
     */
    protected renderContent(): React.JSX.Element {
        return (
            <div className="RichText">
                {this.props.content}

                <style jsx>{`
                    .RichText {
                        /**
                         * Reset top-margin on first item in container, since
                         * there's no content above that there should be created
                         * space between.
                         */
                        & :global(:first-child) {
                            margin-top: 0;
                        }

                        /** ... do the same for the last items bottom-margin */
                        & :global(:last-child) {
                            margin-bottom: 0;
                        }

                        /** Setup styles related to headers */
                        & :global(h1) {
                            font-size: 2em;
                            font-weight: 400;
                            line-height: 1.1em;

                            /** Setup spacing between title and other content */
                            margin-top: 15px;
                            margin-bottom: 15px;
                        }

                        & :global(h2) {
                            font-size: 1.5em;
                            font-weight: 700;
                            line-height: 1.2em;

                            /** Setup spacing between title and other content */
                            margin-top: 15px;
                            margin-bottom: 15px;
                        }

                        & :global(h3) {
                            font-size: 1.5em;
                            font-weight: 400;
                            line-height: 1.2em;

                            /** Setup spacing between title and other content */
                            margin-top: 15px;
                            margin-bottom: 15px;
                        }

                        & :global(h4) {
                            font-size: 1.125em;
                            font-weight: 400;
                            line-height: 1.3em;

                            /** Setup spacing between title and other content */
                            margin-top: 5px;
                            margin-bottom: 5px;
                        }

                        & :global(ol),
                        & :global(ul) {
                            /** Ensure lists are indentent alongside other content */
                            padding-left: 1em;
                        }

                        & :global(a) {
                            /** Turn links orange */
                            color: ${ colors.orange};

                            /** Apply hover effect */
                            text-decoration: none;

                            &:global(:hover) {
                                text-decoration: underline;
                            }
                        }
                    }
                `}</style>
            </div>
        );
    }
}

// tslint:disable-next-line variable-name
export const RichtextBlock = injectStore(RichtextBlockUnwrapped);
